import { useEffect, useState } from 'react';
import { LogoIcon } from '../context/TenantContext';
import { tenant } from '../context/useTenant';

// Session-storage key used to remember that we've already fired one
// automatic reload attempt for a stuck loading state, so we don't
// loop if the reload doesn't help.
const RELOAD_FLAG = 'shadowland.loadReloaded';
// Show a "still loading" hint with a manual reload button if the
// screen has been up this long.
const HINT_AT_MS = 3000;
// Auto-reload once if the screen is still up after this long.
const AUTO_RELOAD_AT_MS = 20000;

export default function LoadingScreen({ autoReload = true }) {
  const [showHint, setShowHint] = useState(false);
  const [reloadStarted, setReloadStarted] = useState(false);
  // Remember, at mount-time only, whether this is a retry after a prior
  // auto-reload. This value is stable for the lifetime of the screen.
  const [alreadyReloaded] = useState(() => (
    typeof window !== 'undefined' && window.sessionStorage?.getItem(RELOAD_FLAG) === '1'
  ));

  useEffect(() => {
    const hintTimer = setTimeout(() => setShowHint(true), HINT_AT_MS);

    // Only arm the auto-reload if we haven't already tried once this
    // session — otherwise we'd loop forever on a genuine failure.
    let reloadTimer = null;
    if (autoReload && !alreadyReloaded) {
      reloadTimer = setTimeout(() => {
        try { window.sessionStorage.setItem(RELOAD_FLAG, '1'); } catch { /* ignore */ }
        setReloadStarted(true);
        // Short delay so the "Reloading the app…" text is visible for
        // a beat before the page actually tears down.
        setTimeout(() => window.location.reload(), 450);
      }, AUTO_RELOAD_AT_MS);
    }

    // Successful mount → unmount cycle means the app finished loading.
    // Clear the flag so the next slow load starts with a fresh budget.
    return () => {
      clearTimeout(hintTimer);
      if (reloadTimer) clearTimeout(reloadTimer);
      try { window.sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
    };
  }, [alreadyReloaded, autoReload]);

  const manualReload = () => {
    try { window.sessionStorage.setItem(RELOAD_FLAG, '1'); } catch { /* ignore */ }
    window.location.reload();
  };

  return (
    <div className="loading-screen" data-loading-screen>
      <div className="loading-brand">
        <div className="loading-icon-wrap">
          <div className="loading-halo" aria-hidden="true" />
          <LogoIcon size={80} className="loading-icon" />
          <div className="loading-ring" aria-hidden="true" />
        </div>
        <div className="loading-text">
          <span className="loading-brand-name">{[tenant.name, tenant.tagline].filter(Boolean).join(' ')}</span>
          <span className="loading-brand-sub">{tenant.fullTagline}</span>
        </div>
        <div className="loading-progress" aria-hidden="true"><span /></div>

        {showHint && (
          <div className="loading-hint">
            {reloadStarted ? (
              <div className="loading-hint-text">Reloading the app…</div>
            ) : alreadyReloaded ? (
              <>
                <div className="loading-hint-text error">
                  Still loading after a refresh. Check your connection or try again.
                </div>
                <button type="button" onClick={manualReload} className="loading-reload-btn">
                  Retry
                </button>
              </>
            ) : (
              <>
                <div className="loading-hint-text">Still loading? Tap to refresh.</div>
                <button type="button" onClick={manualReload} className="loading-reload-btn">
                  Reload
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
